import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Modal from 'react-responsive-modal'
import { useDispatch, useSelector } from 'react-redux'
import styles from './styles.module.scss'
import Button from '../../components/Button'
import Table from '../../components/Table'
import { Select } from '../../components/ModalInputs'
import { getSchoolYears, getSubjects } from '../../actions/common'
import { getApplicantGroupList, getApplicantGroupResults } from '../../actions/applicantGroup'
import Loader from '../../components/Loader'
import Empty from '../../components/Empty'
import Confirmation from '../../components/Confirmation/Confirmation'

const streamOptions = [
  { value: 'basic', name: 'Основной набор' },
  { value: 'additional', name: 'Дополнительный набор' },
]

const classOptions = [
  { value: '', name: '' },
  { value: '5', name: '5' },
  { value: '6', name: '6' },
  { value: '7', name: '7' },
  { value: '8', name: '8' },
]

const ResultsModal = ({ open, closeModal }) => {
  const dispatch = useDispatch()
  const { schoolYears, subjects } = useSelector(state => state.common)
  const { list: groupsList } = useSelector(state => state.applicant_group)

  const [params, setParams] = useState({
    school_year: 0,
    stream: 'basic',
    class_year: '',
    group: 'all',
    subject: 'all'
  })
  const [results, setResults] = useState([])
  const [resultsLoading, setResultsLoading] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)
  const handleClose = useCallback(() => setConfirmClose(true), [])
  const acceptConfirmation = useCallback(() => { closeModal(); setConfirmClose(false) }, [closeModal])
  const rejectConfirmation = useCallback(() => setConfirmClose(false), [])

  useEffect(() => {
    if (!open) return
    dispatch(getSchoolYears())
    dispatch(getSubjects())
  }, [open])

  useEffect(() => {
    if (schoolYears.length && open) {
      const actualYear = schoolYears?.find(el => el.actual === true)?.xp_key
      if (actualYear) setParams(prev => ({ ...prev, school_year: actualYear }))
    }
  }, [schoolYears, open])

  useEffect(() => {
    if (params.school_year && open) {
      dispatch(getApplicantGroupList({ school_year: params.school_year }))
    }
  }, [params.school_year, open])

  const handleChange = (e) => {
    const { name, value } = e.target
    setParams(prev => ({ ...prev, [name]: value }))
  }

  const handleRefresh = async () => {
    setResultsLoading(true)
    const data = await dispatch(getApplicantGroupResults(params))
    setResults(data)
    setResultsLoading(false)
  }

  // Динамические колонки экзаменов из всех строк результата
  const examColumns = useMemo(() => {
    const names = new Set()
    results.forEach(row => row.exams?.forEach(e => names.add(e.name)))
    return [...names]
  }, [results])

  const getExamGrade = (row, name) => {
    const found = row.exams?.find(e => e.name === name)
    return found?.grade ?? ''
  }

  const groupOptions = [
    { value: 'all', name: 'Все' },
    ...(groupsList?.map(g => ({ value: g.id, name: g.name })) || [])
  ]

  const subjectOptions = [
    { value: 'all', name: 'Все' },
    ...(subjects?.map(s => ({ value: s.xp_key ?? s.value, name: s.subject ?? s.name })) || [])
  ]

  const totalCols = 2 + examColumns.length + 1 + 2 + 1

  return (
    <Modal
      open={open}
      onClose={handleClose}
      showCloseIcon={false}
      classNames={{ modal: styles.resultsModal }}
      closeOnEsc={true}
      closeOnOverlayClick={true}
      animationDuration={1}
    >
      <div className={styles.resultsModalHeader}>Результаты испытаний</div>

      <div className={styles.resultsModalContent}>
        <div className={styles.resultsModalFilters}>
          <div className={styles.resultsModalFiltersRow}>
            <div className={styles.resultsModalFilterItem}>
              <span className={styles.resultsModalFilterLabel}>Год</span>
              <Select name='school_year' value={params.school_year} onChange={handleChange} style={{ minWidth: '120px' }}>
                {schoolYears?.map(el => <option key={el.xp_key} value={el.xp_key}>{el.name}</option>)}
              </Select>
            </div>
            <Select name='stream' value={params.stream} onChange={handleChange} style={{ minWidth: '150px' }}>
              {streamOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.name}</option>)}
            </Select>
            <div className={styles.resultsModalFilterItem}>
              <span className={styles.resultsModalFilterLabel}>Класс</span>
              <Select name='class_year' value={params.class_year} onChange={handleChange} style={{ minWidth: '80px' }}>
                {classOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.name}</option>)}
              </Select>
            </div>
          </div>

          <div className={styles.resultsModalFiltersRow}>
            <div className={styles.resultsModalFilterItem}>
              <span className={styles.resultsModalFilterLabel}>Группа</span>
              <Select name='group' value={params.group} onChange={handleChange} style={{ minWidth: '300px' }}>
                {groupOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.name}</option>)}
              </Select>
            </div>
            <Button onClick={handleRefresh}>Обновить</Button>
          </div>

          <div className={styles.resultsModalFiltersRow}>
            <div className={styles.resultsModalFilterItem}>
              <span className={styles.resultsModalFilterLabel}>Предмет</span>
              <Select name='subject' value={params.subject} onChange={handleChange} style={{ minWidth: '300px' }}>
                {subjectOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.name}</option>)}
              </Select>
            </div>
          </div>
        </div>

        {resultsLoading && <div className={styles.resultsModalLoader}><Loader /></div>}

        {!resultsLoading && (
          results.length === 0
            ? <Empty />
            : (
              <Table className={styles.resultsModalTable}>
                <thead>
                  <tr>
                    <th colSpan={2}>Претендент</th>
                    <th colSpan={examColumns.length || 1} className={styles.examHeader}>Экзамены</th>
                    <th>Тесты</th>
                    <th colSpan={2}>Повышающие показат...</th>
                    <th>Ед. балл</th>
                  </tr>
                  <tr>
                    <th>ФИО</th>
                    <th>Дата рождения</th>
                    {examColumns.length > 0
                      ? examColumns.map(name => <th key={name} className={styles.examHeader}>{name}</th>)
                      : <th className={styles.examHeader}></th>
                    }
                    <th>Психолог. тест 1</th>
                    <th>Ср. балл по табелю</th>
                    <th>Олимпиады, конкурсы</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((item, idx) => (
                    <tr key={item.applicant_id ?? idx}>
                      <td>{item.fio}</td>
                      <td>{item.birth_date}</td>
                      {examColumns.length > 0
                        ? examColumns.map(name => <td key={name} className={styles.examCell}>{getExamGrade(item, name)}</td>)
                        : <td className={styles.examCell}></td>
                      }
                      <td>{item.psych_test_1}</td>
                      <td>{item.tabel_avg}</td>
                      <td>{item.olympiads}</td>
                      <td>{item.total_score}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )
        )}

        <div className={styles.resultsModalStatus}>
          Строк: {results.length}
        </div>
      </div>

      <div className={styles.resultsModalFooter}>
        <Button mode='white' onClick={handleClose}>Закрыть</Button>
      </div>
      {confirmClose && (
        <Confirmation
          title="Закрыть окно результатов?"
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
        />
      )}
    </Modal>
  )
}

export default ResultsModal
